import { openDB } from 'idb';
import type { DBSchema, IDBPDatabase } from 'idb';
import type {
  EmergencyContact,
  QueuedMessage,
  ReunionPoint,
  Resource,
  ChecklistItem,
} from '../types';

interface DisasterMeshDB extends DBSchema {
  contacts: {
    key: string;
    value: EmergencyContact;
    indexes: {
      'by-name': string;
      'by-relationship': string;
    };
  };
  messages: {
    key: string;
    value: QueuedMessage;
    indexes: {
      'by-status': string;
      'by-priority': string;
      'by-created': number;
    };
  };
  reunionPoints: {
    key: string;
    value: ReunionPoint;
    indexes: {
      'by-type': string;
    };
  };
  resources: {
    key: string;
    value: Resource;
    indexes: {
      'by-category': string;
      'by-status': string;
    };
  };
  checklist: {
    key: string;
    value: ChecklistItem;
    indexes: {
      'by-category': string;
      'by-priority': string;
      'by-checked': number;
    };
  };
}

const DB_NAME = 'disaster-mesh-db';
const DB_VERSION = 1;

let dbInstance: IDBPDatabase<DisasterMeshDB> | null = null;

export async function getDB(): Promise<IDBPDatabase<DisasterMeshDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<DisasterMeshDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Contacts store
      if (!db.objectStoreNames.contains('contacts')) {
        const contactStore = db.createObjectStore('contacts', { keyPath: 'id' });
        contactStore.createIndex('by-name', 'name');
        contactStore.createIndex('by-relationship', 'relationship');
      }

      // Messages store
      if (!db.objectStoreNames.contains('messages')) {
        const messageStore = db.createObjectStore('messages', { keyPath: 'id' });
        messageStore.createIndex('by-status', 'status');
        messageStore.createIndex('by-priority', 'priority');
        messageStore.createIndex('by-created', 'createdAt');
      }

      // Reunion points store
      if (!db.objectStoreNames.contains('reunionPoints')) {
        const reunionStore = db.createObjectStore('reunionPoints', { keyPath: 'id' });
        reunionStore.createIndex('by-type', 'type');
      }

      // Resources store
      if (!db.objectStoreNames.contains('resources')) {
        const resourceStore = db.createObjectStore('resources', { keyPath: 'id' });
        resourceStore.createIndex('by-category', 'category');
        resourceStore.createIndex('by-status', 'status');
      }

      // Checklist store
      if (!db.objectStoreNames.contains('checklist')) {
        const checklistStore = db.createObjectStore('checklist', { keyPath: 'id' });
        checklistStore.createIndex('by-category', 'category');
        checklistStore.createIndex('by-priority', 'priority');
        checklistStore.createIndex('by-checked', 'isChecked');
      }
    },
  });

  return dbInstance;
}

// Generate unique ID
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Contact Operations
export async function getAllContacts(): Promise<EmergencyContact[]> {
  const db = await getDB();
  return db.getAll('contacts');
}

export async function getContact(id: string): Promise<EmergencyContact | undefined> {
  const db = await getDB();
  return db.get('contacts', id);
}

export async function addContact(contact: Omit<EmergencyContact, 'id' | 'createdAt' | 'updatedAt'>): Promise<EmergencyContact> {
  const db = await getDB();
  const now = Date.now();
  const newContact: EmergencyContact = {
    ...contact,
    id: generateId(),
    createdAt: now,
    updatedAt: now,
  };
  await db.add('contacts', newContact);
  return newContact;
}

export async function updateContact(id: string, updates: Partial<EmergencyContact>): Promise<EmergencyContact | undefined> {
  const db = await getDB();
  const existing = await db.get('contacts', id);
  if (!existing) return undefined;

  const updated: EmergencyContact = {
    ...existing,
    ...updates,
    id,
    updatedAt: Date.now(),
  };
  await db.put('contacts', updated);
  return updated;
}

export async function deleteContact(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('contacts', id);
}

// Message Operations
export async function getAllMessages(): Promise<QueuedMessage[]> {
  const db = await getDB();
  return db.getAll('messages');
}

export async function getPendingMessages(): Promise<QueuedMessage[]> {
  const db = await getDB();
  return db.getAllFromIndex('messages', 'by-status', 'pending');
}

export async function addMessage(message: Omit<QueuedMessage, 'id' | 'createdAt' | 'updatedAt' | 'status' | 'retryCount'>): Promise<QueuedMessage> {
  const db = await getDB();
  const now = Date.now();
  const newMessage: QueuedMessage = {
    ...message,
    id: generateId(),
    status: 'pending',
    retryCount: 0,
    createdAt: now,
    updatedAt: now,
  };
  await db.add('messages', newMessage);
  return newMessage;
}

export async function updateMessage(id: string, updates: Partial<QueuedMessage>): Promise<QueuedMessage | undefined> {
  const db = await getDB();
  const existing = await db.get('messages', id);
  if (!existing) return undefined;

  const updated: QueuedMessage = {
    ...existing,
    ...updates,
    id,
    updatedAt: Date.now(),
  };
  await db.put('messages', updated);
  return updated;
}

export async function deleteMessage(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('messages', id);
}

// Reunion Point Operations
export async function getAllReunionPoints(): Promise<ReunionPoint[]> {
  const db = await getDB();
  return db.getAll('reunionPoints');
}

export async function addReunionPoint(point: Omit<ReunionPoint, 'id' | 'createdAt' | 'updatedAt'>): Promise<ReunionPoint> {
  const db = await getDB();
  const now = Date.now();
  const newPoint: ReunionPoint = {
    ...point,
    id: generateId(),
    createdAt: now,
    updatedAt: now,
  };
  await db.add('reunionPoints', newPoint);
  return newPoint;
}

export async function updateReunionPoint(id: string, updates: Partial<ReunionPoint>): Promise<ReunionPoint | undefined> {
  const db = await getDB();
  const existing = await db.get('reunionPoints', id);
  if (!existing) return undefined;

  const updated: ReunionPoint = {
    ...existing,
    ...updates,
    id,
    updatedAt: Date.now(),
  };
  await db.put('reunionPoints', updated);
  return updated;
}

export async function deleteReunionPoint(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('reunionPoints', id);
}

// Resource Operations
export async function getAllResources(): Promise<Resource[]> {
  const db = await getDB();
  return db.getAll('resources');
}

export async function addResource(resource: Omit<Resource, 'id' | 'createdAt' | 'updatedAt'>): Promise<Resource> {
  const db = await getDB();
  const now = Date.now();
  const newResource: Resource = {
    ...resource,
    id: generateId(),
    createdAt: now,
    updatedAt: now,
  };
  await db.add('resources', newResource);
  return newResource;
}

export async function updateResource(id: string, updates: Partial<Resource>): Promise<Resource | undefined> {
  const db = await getDB();
  const existing = await db.get('resources', id);
  if (!existing) return undefined;

  const updated: Resource = {
    ...existing,
    ...updates,
    id,
    updatedAt: Date.now(),
  };
  await db.put('resources', updated);
  return updated;
}

export async function deleteResource(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('resources', id);
}

// Checklist Operations
export async function getAllChecklistItems(): Promise<ChecklistItem[]> {
  const db = await getDB();
  return db.getAll('checklist');
}

export async function addChecklistItem(item: Omit<ChecklistItem, 'id' | 'createdAt' | 'updatedAt'>): Promise<ChecklistItem> {
  const db = await getDB();
  const now = Date.now();
  const newItem: ChecklistItem = {
    ...item,
    id: generateId(),
    createdAt: now,
    updatedAt: now,
  };
  await db.add('checklist', newItem);
  return newItem;
}

export async function updateChecklistItem(id: string, updates: Partial<ChecklistItem>): Promise<ChecklistItem | undefined> {
  const db = await getDB();
  const existing = await db.get('checklist', id);
  if (!existing) return undefined;

  const updated: ChecklistItem = {
    ...existing,
    ...updates,
    id,
    updatedAt: Date.now(),
  };
  await db.put('checklist', updated);
  return updated;
}

export async function deleteChecklistItem(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('checklist', id);
}

// Bulk operations for initial data seeding
export async function seedDefaultChecklist(): Promise<void> {
  const db = await getDB();
  const existingItems = await db.getAll('checklist');

  if (existingItems.length > 0) return;

  const defaultItems: Omit<ChecklistItem, 'id' | 'createdAt' | 'updatedAt'>[] = [
    // Water
    { name: 'Drinking Water', category: 'Water', quantity: 0, quantityNeeded: 3, unit: 'gallons/person/day', isChecked: false, priority: 'essential' },
    { name: 'Water Purification Tablets', category: 'Water', quantity: 0, quantityNeeded: 50, unit: 'tablets', isChecked: false, priority: 'important' },
    { name: 'Water Filter', category: 'Water', quantity: 0, quantityNeeded: 1, unit: 'unit', isChecked: false, priority: 'important' },

    // Food
    { name: 'Canned Food', category: 'Food', quantity: 0, quantityNeeded: 14, unit: 'cans', isChecked: false, priority: 'essential' },
    { name: 'Dry Goods (rice, pasta)', category: 'Food', quantity: 0, quantityNeeded: 10, unit: 'lbs', isChecked: false, priority: 'essential' },
    { name: 'Energy Bars', category: 'Food', quantity: 0, quantityNeeded: 12, unit: 'bars', isChecked: false, priority: 'important' },
    { name: 'Manual Can Opener', category: 'Food', quantity: 0, quantityNeeded: 1, unit: 'unit', isChecked: false, priority: 'essential' },

    // Medical
    { name: 'First Aid Kit', category: 'Medical', quantity: 0, quantityNeeded: 1, unit: 'kit', isChecked: false, priority: 'essential' },
    { name: 'Prescription Medications', category: 'Medical', quantity: 0, quantityNeeded: 7, unit: 'days supply', isChecked: false, priority: 'essential' },
    { name: 'Pain Relievers', category: 'Medical', quantity: 0, quantityNeeded: 50, unit: 'tablets', isChecked: false, priority: 'important' },
    { name: 'Bandages', category: 'Medical', quantity: 0, quantityNeeded: 20, unit: 'pieces', isChecked: false, priority: 'essential' },

    // Shelter
    { name: 'Emergency Blankets', category: 'Shelter', quantity: 0, quantityNeeded: 4, unit: 'blankets', isChecked: false, priority: 'essential' },
    { name: 'Tent or Tarp', category: 'Shelter', quantity: 0, quantityNeeded: 1, unit: 'unit', isChecked: false, priority: 'important' },
    { name: 'Sleeping Bags', category: 'Shelter', quantity: 0, quantityNeeded: 4, unit: 'bags', isChecked: false, priority: 'important' },

    // Communication
    { name: 'Battery/Hand Crank Radio', category: 'Communication', quantity: 0, quantityNeeded: 1, unit: 'unit', isChecked: false, priority: 'essential' },
    { name: 'Whistle', category: 'Communication', quantity: 0, quantityNeeded: 4, unit: 'whistles', isChecked: false, priority: 'essential' },
    { name: 'Charged Power Banks', category: 'Communication', quantity: 0, quantityNeeded: 2, unit: 'units', isChecked: false, priority: 'important' },

    // Light & Power
    { name: 'Flashlights', category: 'Light & Power', quantity: 0, quantityNeeded: 4, unit: 'units', isChecked: false, priority: 'essential' },
    { name: 'Extra Batteries', category: 'Light & Power', quantity: 0, quantityNeeded: 20, unit: 'batteries', isChecked: false, priority: 'essential' },
    { name: 'Candles', category: 'Light & Power', quantity: 0, quantityNeeded: 10, unit: 'candles', isChecked: false, priority: 'optional' },
    { name: 'Matches/Lighters', category: 'Light & Power', quantity: 0, quantityNeeded: 3, unit: 'units', isChecked: false, priority: 'essential' },

    // Tools
    { name: 'Multi-tool or Knife', category: 'Tools', quantity: 0, quantityNeeded: 1, unit: 'unit', isChecked: false, priority: 'essential' },
    { name: 'Duct Tape', category: 'Tools', quantity: 0, quantityNeeded: 2, unit: 'rolls', isChecked: false, priority: 'important' },
    { name: 'Work Gloves', category: 'Tools', quantity: 0, quantityNeeded: 2, unit: 'pairs', isChecked: false, priority: 'important' },

    // Documents
    { name: 'Copies of Important Documents', category: 'Documents', quantity: 0, quantityNeeded: 1, unit: 'set', isChecked: false, priority: 'essential' },
    { name: 'Emergency Cash', category: 'Documents', quantity: 0, quantityNeeded: 200, unit: 'dollars', isChecked: false, priority: 'essential' },
    { name: 'Emergency Contact List', category: 'Documents', quantity: 0, quantityNeeded: 1, unit: 'copy', isChecked: false, priority: 'essential' },

    // Hygiene
    { name: 'Toilet Paper', category: 'Hygiene', quantity: 0, quantityNeeded: 6, unit: 'rolls', isChecked: false, priority: 'important' },
    { name: 'Hand Sanitizer', category: 'Hygiene', quantity: 0, quantityNeeded: 2, unit: 'bottles', isChecked: false, priority: 'important' },
    { name: 'Toothbrush & Toothpaste', category: 'Hygiene', quantity: 0, quantityNeeded: 4, unit: 'sets', isChecked: false, priority: 'important' },
  ];

  for (const item of defaultItems) {
    await addChecklistItem(item);
  }
}
